import React, { useState, useEffect, useMemo } from 'react';
import { Page } from '../../../layout';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import {
  EmailRegEx,
  UsernameRegexEx,
  EmailMaxLength,
  firstNameMaxLength,
  lastNameMaxLength
} from '../../../../constants/validation';
import { Button, Checkbox, Collapse } from '@mui/material';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import ApolloError from '../../../shared/components/ApolloError';
import SEARCH_USER_BY_EMAIL from '../../queries/searchUserByEmailQuery';
import ASSIGN_LICENSE_TO_USER from '../../queries/assignLicenseToUserMutation';
import ASSIGN_APPRAISER from '../../queries/assignAppraiserMutation';
import { isUserAdminTier } from '../../../shared/services/checkUserTierService';
import CustomSelector from '../../../shared/components/CustomSelector';
import PageHeader from './PageHeader';
import Asterisk from '../../../shared/components/Asterisk';
import CheckedXIcon from '../../../shared/components/CheckedXIcon';
import { userPrivileges, userTier } from '../../../../apollo/cache';
import SkeletonWrapper from '../../../shared/components/skeleton/SkeletonWrapper';
import { ISACA_MDDAP_APPRAISER, VIP_PORTAL_ADMIN } from '../../../../constants/system-roles';
import * as LicenseTypes from '../../constants/LicenseTypes';
import PermissionLists from '../shared/components/PermissionLists/PermissionLists';
import { Breadcrumb } from '../../../shared/components/breadcrumb/Breadcrumb';
import { getPageTitle } from '../../../shared/services/pageTitleService';
import { useUserRoleOptions } from '../shared/hooks/useUserRoleOptions';
import { getUserAssignEditPermissions } from '../shared/services/userAssignEditPermissionsService';
import LinkButton from '../../../shared/components/LinkButton';
import { PAGE_MODE } from '../../constants/pageModeTypes';
import UserLicenseInformationInputValidator from '../../validators/userLicenseInformationInputValidator';
import { DEFAULT_APPRAISER_DATA } from '../../../AppraiserForm/constants/AppraiserInfoConstants';
import AppraiserInputValidator from '../../../AppraiserForm/validators/AppraiserInputValidator';
import AppraiserDataMapper from '../../../AppraiserForm/mappers/AppraiserDataMapper';
import AppraiserInfo from './AppraiserInfo/AppraiserInfo';
import { APPRAISER_SYSTEM_ROLES } from '../shared/constants/appraiserSystemRoles';
import _ from 'lodash';
import AtmCheckbox from '../shared/components/AtmCheckbox';
import LicenseDataMapper from '../../mappers/LicenseDataMapper';
import ViewInvoicesCheckbox from '../shared/components/ViewInvoicesCheckbox';

const AssignLicensePage = () => {
  const location = useLocation();
  const { pageMode, orgId, orgUnitId, isFree, orgName } = location.state || {};

  const currentUserTier = useReactiveVar(userTier);
  const privileges = useReactiveVar(userPrivileges);
  const [userEmail, setUserEmail] = useState('');
  const [userEmailError, setUserEmailError] = useState('');
  const [userEmailResultMessage, setUserEmailResultMessage] = useState('');
  const [submittedUserEmail, setSubmittedUserEmail] = useState(null);
  const [showDataFields, setShowDataFields] = useState(false);
  const [userData, setUserData] = useState({
    externalUserId: 0,
    email: '',
    firstName: '',
    lastName: '',
    selectedRole: getDefaultRole(pageMode)
  });
  const [customPermissionData, setCustomPermissionData] = useState(DEFAULT_CUSTOM_PERMISSIONS);
  const [disableUserRoleMenuItems, setDisableUserRoleMenuItems] = useState(true);
  const [disableSubmitButton, setDisableSubmitButton] = useState(true);
  const [assignLicenseErrorMessage, setAssignLicenseErrorMessage] = useState('');
  const [permissionData, setPermissionData] = useState(
    /** @type {typeof DEFAULT_PERMISSIONS | undefined} */ (undefined)
  );
  const [appraiserData, setAppraiserData] = useState(
    /** @type {typeof DEFAULT_APPRAISER_DATA | undefined} */ (undefined)
  );

  const {
    isUserAbleToSetSuperUserCheckbox,
    isUserAbleToSetATMCheckbox,
    isUserAbleToSetViewInvoicesCheckbox,
    shouldShowRoleAndPermissions,
    shouldShowPermissions
  } = getUserAssignEditPermissions(
    currentUserTier,
    pageMode,
    privileges,
    isFree,
    userData.selectedRole
  );

  const isAppraiserLicense =
    customPermissionData.isAtm || APPRAISER_SYSTEM_ROLES.includes(userData.selectedRole);

  const pageTitle = getPageTitle([orgName, 'Assign License']);
  const annotationItems = orgName ? [orgName] : [];
  const previousLink = isUserAdminTier(currentUserTier)
    ? `/user-management/${orgId}`
    : '/user-management';
  const breadcrumbPathItems = [
    {
      name: 'User Management',
      url: previousLink
    },
    { name: 'Assign License', url: '' }
  ];

  const navigate = useNavigate();

  const disableSearchButton = useMemo(() => {
    return _.isEqual(userEmail, submittedUserEmail);
  }, [userEmail, submittedUserEmail]);

  const [searchUser, { data: searchData, loading: searchLoading, error: searchError }] =
    useLazyQuery(SEARCH_USER_BY_EMAIL, {
      fetchPolicy: 'no-cache'
    });

  const [assignLicenseToUser, { error: assignLicenseToUserError }] = useMutation(
    ASSIGN_LICENSE_TO_USER,
    {
      update: cache => {
        cache.evict({});
      }
    }
  );

  const [assignAppraiser, { error: assignAppraiserError }] = useMutation(ASSIGN_APPRAISER, {
    update: cache => {
      cache.evict({});
    }
  });

  const { userRoleOptions, userRoleOptionsLoading, userRoleOptionsError } = useUserRoleOptions(
    orgId,
    orgUnitId,
    isFree,
    !shouldShowRoleAndPermissions || !searchData
  );

  const isUserInfoValid =
    userData &&
    !UserLicenseInformationInputValidator.validateUserData(userData).error &&
    (!isAppraiserLicense ||
      !AppraiserInputValidator.validateAssignLicenseToAppraiserData(appraiserData).error);

  useEffect(() => {
    if (!userData) {
      return;
    }

    if (isUserInfoValid) {
      setDisableSubmitButton(false);

      return;
    }

    setDisableSubmitButton(true);
  }, [userData, permissionData, appraiserData, isAppraiserLicense, isUserInfoValid]);

  const handleEmailChange = event => {
    setUserEmail(event.target.value);

    if (userEmailError) {
      setUserEmailError(undefined);
    }
  };

  const handleAssignCallback = data => {
    if (!data.Success) {
      setAssignLicenseErrorMessage(data.Message);
      return;
    }

    navigate(`${previousLink}#${isFree ? LicenseTypes.FREE.value : LicenseTypes.PAID.value}`, {
      state: {
        licensesModifiedSuccessfullyMessage: data.Message
      }
    });
  };

  const setFreeFields = () => {
    setPermissionData(DEFAULT_PERMISSIONS);
    setCustomPermissionData(DEFAULT_CUSTOM_PERMISSIONS);
    setDisableUserRoleMenuItems(false);
    setDisableSubmitButton(false);
    setShowDataFields(true);
  };

  const setPaidFields = () => {
    setCustomPermissionData({
      superUser: pageMode === PAGE_MODE.ISACAVipOrg,
      canAccessInvoices: false,
      isAtm: false
    });
    setDisableUserRoleMenuItems(false);
    setDisableSubmitButton(true);
    setShowDataFields(true);
  };

  const handleEmailSubmit = async event => {
    event.preventDefault();
    setSubmittedUserEmail(userEmail);

    if (!isEmailValid(userEmail)) {
      setUserEmailError('You have entered an invalid email address!');
      resetFields(false);
      return;
    }

    if (!isUsernameValid(userEmail)) {
      setUserEmailError('The given email is not a valid username!');
      resetFields(false);
      return;
    }

    resetFields(true);
    setUserEmailError(undefined);
    const searchUserResult = await searchUser({
      variables: { email: userEmail }
    });

    const resultMessage =
      searchUserResult.data.searchUserByEmail === null
        ? 'User does not exist. Please complete the following information to assign the license to a new user. ' +
          'Please confirm that the user has received a password reset email.'
        : 'Confirmed existing user. Please complete the following information to assign the license to the user.';

    setUserEmailResultMessage(resultMessage);
    setUserData(getUserData(searchUserResult.data.searchUserByEmail, userEmail, pageMode));

    isFree ? setFreeFields() : setPaidFields();
  };

  const resetFields = showDataFields => {
    const defaultAppraiserFormData = _.cloneDeep(DEFAULT_APPRAISER_DATA);

    setUserEmailResultMessage('');
    setAssignLicenseErrorMessage('');
    setShowDataFields(showDataFields);
    setPermissionData(DEFAULT_PERMISSIONS);
    setAppraiserData(defaultAppraiserFormData);
  };

  const handleFormSubmit = async event => {
    event.preventDefault();
    setAssignLicenseErrorMessage('');

    if (!isUserInfoValid) {
      return;
    }

    const userInfo = LicenseDataMapper.licenseDataToApolloInput(
      userData,
      orgId,
      orgUnitId,
      permissionData,
      customPermissionData,
      isUserAbleToSetSuperUserCheckbox,
      isUserAbleToSetViewInvoicesCheckbox
    );

    if (isAppraiserLicense) {
      const result = await assignAppraiser({
        variables: {
          user: userInfo,
          appraiser: AppraiserDataMapper.appraiserDataToApolloInput(appraiserData)
        }
      });

      handleAssignCallback(result.data.assignAppraiser);
      return;
    }

    const result = await assignLicenseToUser({ variables: { user: userInfo } });

    handleAssignCallback(result.data.assignLicenseToUser);
  };

  const handleChange = event => {
    event.preventDefault();
    let name = event.target.name;
    let value = event.target.value;
    if (
      (name == 'firstName' && !isFirstNameValid(value)) ||
      (name == 'lastName' && !isLastNameValid(value))
    ) {
      return;
    }
    setUserData({ ...userData, [name]: value });
  };

  const setSelectedRole = role => {
    setUserData({ ...userData, selectedRole: role });
  };

  const handleSuperUserCheckboxClick = value => {
    updateCustomPermission(CUSTOM_PERMISSION_KEYS.SUPER_USER, value);

    if (value) {
      setPermissionData({ ohts: [], orgIds: [], orgUnitIds: [] });
    }
  };

  const updateCustomPermission = (permission, value) => {
    setCustomPermissionData(prev => ({
      ...prev,
      [permission]: value
    }));
  };

  if (!location.state) {
    const redirectionLink = isUserAdminTier(currentUserTier)
      ? '/user-management/organization-search'
      : '/user-management';

    return <Navigate to={redirectionLink} />;
  }

  if (assignLicenseToUserError || assignAppraiserError || searchError || userRoleOptionsError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle}>
      <Breadcrumb pathItems={breadcrumbPathItems} annotationItems={annotationItems} />

      <div className='license-page'>
        <PageHeader
          pageMode={pageMode}
          isAppraiserLicense={isAppraiserLicense}
          hasAccessToAscDomain={userData.selectedRole === ISACA_MDDAP_APPRAISER}
        />
        <h4 data-testid='emailField' className='field-title'>
          Email
        </h4>
        <form className='assign-license-form' onSubmit={handleFormSubmit}>
          <div role='emailForm' className='email-form'>
            <input
              className='input-field'
              id='email'
              type='text'
              placeholder='Enter an email address.'
              value={userEmail}
              onChange={handleEmailChange}
            />
            <Button
              className='primary-button sm'
              onClick={handleEmailSubmit}
              type='button'
              disabled={disableSearchButton}>
              Search
            </Button>
          </div>
          {userEmailError && (
            <ErrorMessage
              dataTestId='emailErrorMessage'
              message={userEmailError}
              className='email-input-error'
            />
          )}

          {showDataFields && (
            <>
              {userEmailResultMessage && userEmailResultMessage !== '' && (
                <p className='result-message'>{userEmailResultMessage}</p>
              )}
              <h4 className='field-title'>
                First Name
                <Asterisk />
              </h4>
              <SkeletonWrapper show={searchLoading}>
                <input
                  className={'input-field' + (userData.externalUserId !== 0 ? ' disabled' : '')}
                  id='first-name'
                  type='text'
                  name='firstName'
                  placeholder='Enter First Name'
                  value={userData.firstName}
                  onChange={handleChange}
                  disabled={userData.externalUserId !== 0}
                  required
                />
              </SkeletonWrapper>

              <h4 className='field-title'>
                Last Name
                <Asterisk />
              </h4>
              <SkeletonWrapper show={searchLoading}>
                <input
                  className={'input-field' + (userData.externalUserId !== 0 ? ' disabled' : '')}
                  id='last-name'
                  type='text'
                  name='lastName'
                  placeholder='Enter Last Name'
                  value={userData.lastName}
                  onChange={handleChange}
                  disabled={userData.externalUserId !== 0}
                  required
                />
              </SkeletonWrapper>

              {shouldShowRoleAndPermissions && (
                <>
                  {pageMode !== PAGE_MODE.ISACAVipOrg && (
                    <CustomSelector
                      id='roles-selector'
                      title='User Role'
                      placeholder='Select a User Role'
                      testId='roles-selector'
                      options={userRoleOptions}
                      disabled={disableUserRoleMenuItems}
                      value={userData.selectedRole}
                      setValue={setSelectedRole}
                      loading={userRoleOptionsLoading || searchLoading}
                    />
                  )}

                  {isUserAbleToSetSuperUserCheckbox && (
                    <div className='checkbox-wrapper'>
                      <h4 className='field-title'>Super User?</h4>
                      <Checkbox
                        id='super-user-checkbox'
                        data-testid='super-user-checkbox'
                        size='small'
                        checked={customPermissionData[CUSTOM_PERMISSION_KEYS.SUPER_USER]}
                        checkedIcon={<CheckedXIcon size='small' />}
                        onChange={e => handleSuperUserCheckboxClick(e.target.checked)}
                        color='default'
                      />
                    </div>
                  )}

                  {isUserAbleToSetViewInvoicesCheckbox && (
                    <ViewInvoicesCheckbox
                      checked={customPermissionData[CUSTOM_PERMISSION_KEYS.VIEW_INVOICES]}
                      onChange={e =>
                        updateCustomPermission(
                          CUSTOM_PERMISSION_KEYS.VIEW_INVOICES,
                          e.target.checked
                        )
                      }
                    />
                  )}

                  {isUserAbleToSetATMCheckbox && (
                    <AtmCheckbox
                      pageMode={pageMode}
                      isAtm={customPermissionData[CUSTOM_PERMISSION_KEYS.IS_ATM]}
                      setIsAtm={value =>
                        updateCustomPermission(CUSTOM_PERMISSION_KEYS.IS_ATM, value)
                      }
                    />
                  )}
                </>
              )}

              <Collapse in={isAppraiserLicense} unmountOnExit>
                <AppraiserInfo
                  appraiserData={appraiserData}
                  setAppraiserData={setAppraiserData}
                  appraiserSystemRoleId={userData.selectedRole}
                />
              </Collapse>

              {shouldShowPermissions &&
                (pageMode === PAGE_MODE.MddapOrg || userData.selectedRole) && (
                  <PermissionLists
                    pageMode={pageMode}
                    orgId={orgId}
                    isUserRoleSelected={userData.selectedRole !== ''}
                    permissions={permissionData}
                    setPermissions={setPermissionData}
                    disabled={customPermissionData[CUSTOM_PERMISSION_KEYS.SUPER_USER]}
                  />
                )}

              <div className='buttons'>
                <LinkButton
                  id='cancel-button'
                  className='secondary-button sm'
                  to={previousLink}
                  type='button'>
                  Cancel
                </LinkButton>
                <Button
                  id='assign-button'
                  data-testid='assign-button'
                  type='submit'
                  className='primary-button sm'
                  disabled={disableSubmitButton}>
                  Assign
                </Button>
              </div>
              {assignLicenseErrorMessage && assignLicenseErrorMessage !== '' && (
                <p className='assigning-message error-message'>{assignLicenseErrorMessage}</p>
              )}
            </>
          )}
        </form>
      </div>
    </Page>
  );
};

const DEFAULT_PERMISSIONS = {
  ohts: [],
  orgIds: [],
  orgUnitIds: []
};

const CUSTOM_PERMISSION_KEYS = {
  VIEW_INVOICES: 'canAccessInvoices',
  SUPER_USER: 'superUser',
  IS_ATM: 'isAtm'
};

const DEFAULT_CUSTOM_PERMISSIONS = {
  [CUSTOM_PERMISSION_KEYS.VIEW_INVOICES]: false,
  [CUSTOM_PERMISSION_KEYS.SUPER_USER]: false,
  [CUSTOM_PERMISSION_KEYS.IS_ATM]: false
};

const isEmailValid = input => !!input && EmailRegEx.test(input) && input.length <= EmailMaxLength;

const isUsernameValid = input => UsernameRegexEx.test(input);

const isFirstNameValid = input => input.length <= firstNameMaxLength;

const isLastNameValid = input => input.length <= lastNameMaxLength;

const getUserData = (kenticoUserData, userEmail, pageMode) => {
  return {
    externalUserId: kenticoUserData === null ? 0 : kenticoUserData.ExternalUserId,
    firstName: kenticoUserData === null ? '' : kenticoUserData.FirstName,
    lastName: kenticoUserData === null ? '' : kenticoUserData.LastName,
    selectedRole: getDefaultRole(pageMode),
    email: userEmail
  };
};

const getDefaultRole = pageMode => {
  if (pageMode === PAGE_MODE.ISACAAppraiser) return ISACA_MDDAP_APPRAISER;
  if (pageMode === PAGE_MODE.ISACAVipOrg) return VIP_PORTAL_ADMIN;
  return '';
};

export default AssignLicensePage;
